import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useYouTubeAccountReset } from '@/lib/youtubeConnection';
import { formatApiError } from '@/lib/formatApiError';
import { formatCount, rangeQuery } from '@/lib/channel';
import { SeoScoreRing } from '@/components/channel/SeoScoreRing';
import { ChannelSectionNav } from '@/components/channel/ChannelSectionNav';
import { VideoThumb } from '@/components/channel/VideoThumb';
import { RangeSwitcher } from '@/components/channel/RangeSwitcher';

export function ChannelVideosPage() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState('28d');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [videoFormat, setVideoFormat] = useState('all');
  const [privacy, setPrivacy] = useState('all');
  const [source, setSource] = useState('all');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [tagsAdd, setTagsAdd] = useState('');
  const [bulkPrivacy, setBulkPrivacy] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    if (preset === 'custom' && (!start || !end)) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get('/channel/videos', {
        params: {
          ...rangeQuery(preset, start, end),
          format: videoFormat,
          privacy,
          source,
          q: query,
          limit: 100,
        },
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not load videos'));
    } finally {
      setLoading(false);
    }
  }, [preset, start, end, videoFormat, privacy, source, query]);

  const clearChannel = useCallback(() => {
    setItems([]);
    setSelected({});
  }, []);
  useYouTubeAccountReset(clearChannel);

  useEffect(() => {
    const handle = setTimeout(load, query ? 250 : 0);
    return () => clearTimeout(handle);
  }, [load, query]);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);

  const applyBulk = async () => {
    if (!selectedIds.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post('/channel/videos/bulk', {
        video_ids: selectedIds,
        tags_add: tagsAdd.split(/[,#]+/).map((tag) => tag.trim()).filter(Boolean),
        privacy_status: bulkPrivacy || null,
      });
      const errors = data?.errors || [];
      if (errors.length) toast.error(`${errors.length} video${errors.length === 1 ? '' : 's'} could not be updated`);
      else toast.success('Updated selected videos');
      setSelected({});
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'Bulk update failed'));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <main className="p-4 sm:p-8 space-y-4 font-jakarta overflow-x-hidden">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <h1 className="text-lg font-bold text-white">Videos</h1>
          <ChannelSectionNav />
        </div>
        <RangeSwitcher
          preset={preset}
          start={start}
          end={end}
          onChange={(next) => {
            setPreset(next.preset);
            setStart(next.start || '');
            setEnd(next.end || '');
          }}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <SelectPill label="Format" value={videoFormat} onChange={setVideoFormat} options={[['all', 'All'], ['shorts', 'Shorts'], ['long', 'Long']]} />
        <SelectPill label="Privacy" value={privacy} onChange={setPrivacy} options={[['all', 'All'], ['public', 'Public'], ['unlisted', 'Unlisted'], ['private', 'Private']]} />
        <SelectPill label="Source" value={source} onChange={setSource} options={[['all', 'Whole channel'], ['twinnee', 'From Twinnee']]} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search titles"
          className="h-8 px-3 rounded-full bg-[#12151d] border border-[#1f2635] text-xs text-white w-48"
        />
      </div>

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-[14px] border border-[#214f48] bg-[#10211e] px-3 py-2">
          <span className="text-xs text-white">{selectedIds.length} selected</span>
          <input
            value={tagsAdd}
            onChange={(event) => setTagsAdd(event.target.value)}
            placeholder="Tags to add"
            className="h-8 px-3 rounded-full bg-[#0c1018] border border-[#1f2635] text-xs text-white w-48"
          />
          <select
            value={bulkPrivacy}
            onChange={(event) => setBulkPrivacy(event.target.value)}
            className="h-8 px-2 rounded-full bg-[#0c1018] border border-[#1f2635] text-xs text-white"
          >
            <option value="">Privacy unchanged</option>
            <option value="public">Public</option>
            <option value="unlisted">Unlisted</option>
            <option value="private">Private</option>
          </select>
          <button
            type="button"
            onClick={applyBulk}
            disabled={bulkBusy}
            className="h-8 px-3 rounded-full bg-[#34d399] text-[#06211b] text-xs font-bold inline-flex items-center gap-1"
          >
            {bulkBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Apply
          </button>
        </div>
      )}

      <div className="rounded-[18px] border border-[#1f2635] overflow-hidden">
        <table className="w-full text-left text-xs">
          <thead className="bg-[#0c1018] text-[#7d8799]">
            <tr>
              <th className="p-3 w-8" />
              <th className="p-3 font-semibold">Title</th>
              <th className="p-3 font-semibold">Privacy</th>
              <th className="p-3 font-semibold">Views in range</th>
              <th className="p-3 font-semibold">Lifetime</th>
              <th className="p-3 font-semibold text-right">SEO</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-6 text-[#7d8799]">Loading videos…</td></tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-6 text-[#7d8799]">
                  No videos yet. Connect and refresh YouTube from the dashboard, or publish from Twinnee.
                </td>
              </tr>
            ) : items.map((video) => (
              <tr key={video.video_id} className="border-t border-[#1f2635] text-white">
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={Boolean(selected[video.video_id])}
                    onChange={(event) => setSelected((prev) => ({ ...prev, [video.video_id]: event.target.checked }))}
                  />
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <VideoThumb video={video} />
                    <div className="min-w-0">
                      <Link to={`/channel/videos/${video.video_id}`} className="block truncate font-semibold hover:text-[#34d399]">
                        {video.title || video.video_id}
                      </Link>
                      <div className="text-[10px] text-[#7d8799]">
                        {video.is_short ? 'Short' : 'Long'}
                        {video.from_twinnee ? ' · Twinnee' : ''}
                        {video.project_path ? (
                          <>
                            {' · '}
                            <Link to={video.project_path} className="underline">Project</Link>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-3 capitalize text-[#c5cedb]">{video.privacy_status}</td>
                <td className="p-3">{formatCount(video.views_in_range)}</td>
                <td className="p-3 text-[#8b95a7]">{formatCount(video.views_lifetime)}</td>
                <td className="p-3">
                  <div className="flex justify-end">
                    <SeoScoreRing
                      score={video.seo_score}
                      size={64}
                      onImprove={() => navigate(`/channel/videos/${video.video_id}?improve=1`)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}

function SelectPill({ label, value, onChange, options }) {
  return (
    <label className="h-8 px-2 rounded-full bg-[#12151d] border border-[#1f2635] text-[11px] text-[#8b95a7] inline-flex items-center gap-1">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="bg-transparent text-white text-xs outline-none"
      >
        {options.map(([id, text]) => (
          <option key={id} value={id} className="bg-[#12151d]">{text}</option>
        ))}
      </select>
    </label>
  );
}

export default ChannelVideosPage;
